import { useState } from 'react'
import { useInstructorCalendar } from '@/hooks/useInstructorCalendar'
import { useStudents } from '@/hooks/useStudents'
import { MonthGrid, type CalendarEvent } from '@/components/calendar/MonthGrid'
import { LessonCard, LessonCardButton } from '@/components/ui/LessonCard'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

function toDateKey(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-CA', { hour: 'numeric', minute: '2-digit' })
}
function toDatetimeLocalValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const sectionStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '20px 22px', marginBottom: 18,
}
const sectionTitleStyle: React.CSSProperties = {
  font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 14,
}

const BOOK_ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'You are not authorized to book this lesson.',
  invalid_time_range: 'Pick a valid start and finish time.',
  invalid_duration: 'A lesson cannot be longer than 8 hours.',
  relationship_not_found: 'That student relationship could not be found.',
  relationship_not_active: 'That student relationship is no longer active.',
  slot_conflict: 'That time overlaps another lesson on your calendar.',
}

const DURATION_OPTIONS = [30, 45, 60, 90, 120]
function durationLabel(m: number) {
  return m < 60 ? `${m} min` : m === 60 ? '1 hr' : `${(m / 60).toFixed(1).replace('.0', '')} hrs`
}

// Quick-pick chips that set Finish time from Start time + duration -- the two datetime
// inputs already let an instructor set ANY duration by hand, but most lessons are one of
// a handful of common lengths, and mental math between two separate pickers for "an hour
// and a half from now" is needless friction for the common case.
function DurationChips({ selected, onPick }: { selected: number | null; onPick: (minutes: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {DURATION_OPTIONS.map(m => (
        <button
          key={m}
          type='button'
          onClick={() => onPick(m)}
          style={{
            padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
            border: `1px solid ${selected === m ? '#0F7C6C' : 'rgba(27,42,74,0.15)'}`,
            background: selected === m ? 'rgba(15,124,108,0.1)' : 'transparent',
            color: selected === m ? '#0F7C6C' : 'rgba(27,42,74,0.6)',
            font: '600 12px Inter, sans-serif',
          }}
        >
          {durationLabel(m)}
        </button>
      ))}
    </div>
  )
}

function BookLessonForm({ onBooked }: { onBooked: () => void }) {
  const { students } = useStudents()
  const { bookLesson, saving } = useInstructorCalendar()
  const [open, setOpen] = useState(false)
  const [relationshipId, setRelationshipId] = useState('')
  const [startAt, setStartAt] = useState(() => toDatetimeLocalValue(new Date(Date.now() + 24 * 60 * 60 * 1000)))
  const [endAt, setEndAt] = useState(() => toDatetimeLocalValue(new Date(Date.now() + 25 * 60 * 60 * 1000)))
  const [selectedDuration, setSelectedDuration] = useState<number | null>(60)
  const [error, setError] = useState('')

  const applyDuration = (minutes: number) => {
    setSelectedDuration(minutes)
    const start = new Date(startAt)
    if (!isNaN(start.getTime())) {
      setEndAt(toDatetimeLocalValue(new Date(start.getTime() + minutes * 60 * 1000)))
    }
  }

  const handleStartChange = (value: string) => {
    setStartAt(value)
    if (selectedDuration !== null) {
      const start = new Date(value)
      if (!isNaN(start.getTime())) {
        setEndAt(toDatetimeLocalValue(new Date(start.getTime() + selectedDuration * 60 * 1000)))
      }
    }
  }

  const handleSubmit = async () => {
    setError('')
    if (!relationshipId) { setError('Pick a student.'); return }
    if (new Date(endAt).getTime() <= new Date(startAt).getTime()) { setError('Finish time must be after the start time.'); return }
    const result = await bookLesson({
      relationshipId,
      startAt: new Date(startAt).toISOString(),
      endAt: new Date(endAt).toISOString(),
    })
    if (!result.ok) { setError(BOOK_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not book this lesson.'); return }
    setOpen(false)
    setRelationshipId('')
    onBooked()
  }

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 16 : 0 }}>
        <div style={sectionTitleStyle}>Book a lesson</div>
        <button onClick={() => setOpen(v => !v)} className='btn-teal' style={{ width: 'auto', padding: '9px 16px' }}>
          {open ? 'Cancel' : '+ Book a lesson'}
        </button>
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Student</div>
            <select value={relationshipId} onChange={e => setRelationshipId(e.target.value)} className='field-input' disabled={saving}>
              <option value=''>Select a student…</option>
              {students.map(s => <option key={s.relationshipId} value={s.relationshipId}>{s.fullName ?? s.email}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Start time</div>
              <input type='datetime-local' value={startAt} onChange={e => handleStartChange(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Finish time</div>
              <input type='datetime-local' value={endAt} onChange={e => { setEndAt(e.target.value); setSelectedDuration(null) }} className='field-input' disabled={saving} />
            </div>
          </div>
          <div>
            <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Duration</div>
            <DurationChips selected={selectedDuration} onPick={applyDuration} />
          </div>
          {error && <div className='banner-error'>{error}</div>}
          <button className='btn-teal' onClick={handleSubmit} disabled={saving} style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}>
            {saving ? 'Booking…' : 'Book lesson'}
          </button>
        </div>
      )}
    </div>
  )
}

function RescheduleForm({ lessonId, onDone }: { lessonId: string; onDone: () => void }) {
  const { rescheduleLesson, saving } = useInstructorCalendar()
  const [startAt, setStartAt] = useState(() => toDatetimeLocalValue(new Date(Date.now() + 24 * 60 * 60 * 1000)))
  const [endAt, setEndAt] = useState(() => toDatetimeLocalValue(new Date(Date.now() + 25 * 60 * 60 * 1000)))
  const [selectedDuration, setSelectedDuration] = useState<number | null>(60)
  const [error, setError] = useState('')

  const applyDuration = (minutes: number) => {
    setSelectedDuration(minutes)
    const start = new Date(startAt)
    if (!isNaN(start.getTime())) {
      setEndAt(toDatetimeLocalValue(new Date(start.getTime() + minutes * 60 * 1000)))
    }
  }

  const handleStartChange = (value: string) => {
    setStartAt(value)
    if (selectedDuration !== null) {
      const start = new Date(value)
      if (!isNaN(start.getTime())) {
        setEndAt(toDatetimeLocalValue(new Date(start.getTime() + selectedDuration * 60 * 1000)))
      }
    }
  }

  const handleSubmit = async () => {
    setError('')
    if (new Date(endAt).getTime() <= new Date(startAt).getTime()) { setError('Finish time must be after the start time.'); return }
    const result = await rescheduleLesson(lessonId, new Date(startAt).toISOString(), new Date(endAt).toISOString())
    if (!result.ok) { setError(BOOK_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not reschedule this lesson.'); return }
    onDone()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, padding: 12, background: 'rgba(27,42,74,0.03)', borderRadius: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input type='datetime-local' value={startAt} onChange={e => handleStartChange(e.target.value)} className='field-input' style={{ flex: '1 1 180px' }} disabled={saving} />
        <input type='datetime-local' value={endAt} onChange={e => { setEndAt(e.target.value); setSelectedDuration(null) }} className='field-input' style={{ flex: '1 1 180px' }} disabled={saving} />
      </div>
      <DurationChips selected={selectedDuration} onPick={applyDuration} />
      {error && <div className='banner-error'>{error}</div>}
      <button className='btn-teal' onClick={handleSubmit} disabled={saving} style={{ width: 'auto', padding: '8px 16px', alignSelf: 'flex-start' }}>
        {saving ? 'Saving…' : 'Confirm new time'}
      </button>
    </div>
  )
}

export default function InstructorCalendar() {
  const { lessons, requests, loading, error, saving, cancelLesson, respondToRequest, refresh } = useInstructorCalendar()
  const [reschedulingId, setReschedulingId] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null)

  const handleCancel = async (lessonId: string) => {
    await cancelLesson(lessonId)
  }

  const events: CalendarEvent[] = [
    ...lessons.map(l => ({
      id: l.id, date: toDateKey(l.scheduledAt), label: l.studentName ?? 'Lesson', color: '#0F7C6C',
    })),
    ...requests.map(r => ({
      id: r.id, date: toDateKey(r.requestedStart), label: `? ${r.studentName ?? 'Request'}`, color: '#D4972B',
    })),
  ]

  const visibleLessons = selectedDate ? lessons.filter(l => toDateKey(l.scheduledAt) === selectedDate) : lessons
  const visibleRequests = selectedDate ? requests.filter(r => toDateKey(r.requestedStart) === selectedDate) : requests

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 20 }}>Calendar</div>

      <div className='cal-layout'>
      <div className='cal-col-left'>
      <MonthGrid events={events} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      {selectedDate && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
            Showing {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <button onClick={() => setSelectedDate(null)} style={{ background: 'none', border: 'none', color: '#0F7C6C', cursor: 'pointer', font: '500 12.5px Inter, sans-serif' }}>
            Show all
          </button>
        </div>
      )}
      </div>

      <div className='cal-col-right'>
      <BookLessonForm onBooked={refresh} />

      {error && <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>}

      {visibleRequests.length > 0 && (
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Pending requests</div>
          {visibleRequests.map(r => (
            <div key={r.id} style={{ border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
              <div style={{ font: '500 14px Inter, sans-serif', color: '#1B2A4A' }}>
                {r.kind === 'reschedule' ? 'Reschedule request' : 'New lesson request'} — {r.studentName ?? 'Student'}
              </div>
              <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', margin: '4px 0 10px' }}>
                {formatDateTime(r.requestedStart)} – {formatTime(r.requestedEnd)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => respondToRequest(r.id, true)}
                  disabled={saving}
                  className='btn-teal'
                  style={{ width: 'auto', padding: '8px 16px' }}
                >
                  Confirm
                </button>
                <button
                  onClick={() => respondToRequest(r.id, false)}
                  disabled={saving}
                  style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(192,82,42,0.3)', borderRadius: 8, color: '#C0522A', cursor: 'pointer', font: '500 13px Inter, sans-serif' }}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Upcoming lessons</div>
        {loading && <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}
        {!loading && visibleLessons.length === 0 && (
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>{selectedDate ? 'No lessons on this date.' : 'No upcoming lessons booked.'}</div>
        )}
        {!loading && visibleLessons.map(l => (
          <LessonCard
            key={l.id}
            title={l.studentName ?? 'Student'}
            meta={`${formatDateTime(l.scheduledAt)}${l.plannedEndAt ? ` – ${formatTime(l.plannedEndAt)}` : ''}`}
          >
            <LessonCardButton label='Reschedule' onClick={() => setReschedulingId(id => id === l.id ? null : l.id)} />
            <LessonCardButton label='Cancel' tone='danger' disabled={saving} onClick={() => setConfirmCancelId(l.id)} />
            {reschedulingId === l.id && (
              <div style={{ flexBasis: '100%' }}>
                <RescheduleForm lessonId={l.id} onDone={() => { setReschedulingId(null); refresh() }} />
              </div>
            )}
          </LessonCard>
        ))}
      </div>
      </div>
      </div>

      <ConfirmDialog
        open={!!confirmCancelId}
        title='Cancel this lesson?'
        message="This will cancel the lesson and let your student know. This can't be undone — you'd need to book a new time."
        onCancel={() => setConfirmCancelId(null)}
        onConfirm={() => { handleCancel(confirmCancelId!); setConfirmCancelId(null) }}
      />
    </div>
  )
}
